import {
    Button,
    Grid,
    Link,
    Typography,
    IconButton,
    MenuItem,
    TextField,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Box } from '@mui/system';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

// Mirrors the "extra" categories of the backend OrderDocTypeEnum (the fixed
// INVOICE / EWAY_BILL / PO_COPY slots are handled separately).
export const EXTRA_DOC_CATEGORIES = [
    { value: 'APPOINTMENT_LETTER', label: 'Appointment Letter' },
    { value: 'GATE_PASS', label: 'Gate Pass' },
    { value: 'POD_COPY', label: 'POD Copy' },
    { value: 'OTHER', label: 'Other' },
];

// RTO-specific categories (used by the Mark-RTO popup).
export const RTO_DOC_CATEGORIES = [
    { value: 'RTO_INVOICE', label: 'RTO Invoice' },
    { value: 'RTO_EWAY_BILL', label: 'RTO E-Way Bill' },
    { value: 'OTHER', label: 'Other' },
];

export const MAX_EXTRA_DOCS = 10;

// Labels for every known doc type, so a saved doc renders correctly in any view surface
// regardless of which editor (and category subset) created it.
const DOC_TYPE_LABELS = {
    INVOICE: 'Invoice',
    EWAY_BILL: 'E-Way Bill',
    PO_COPY: 'PO Copy',
    APPOINTMENT_LETTER: 'Appointment Letter',
    GATE_PASS: 'Gate Pass',
    POD_COPY: 'POD Copy',
    RTO_INVOICE: 'RTO Invoice',
    RTO_EWAY_BILL: 'RTO E-Way Bill',
    OTHER: 'Other',
};

export function categoryLabel(docType) {
    return DOC_TYPE_LABELS[docType] || docType;
}

// Maps a saved UploadedDoc from the server into an editor row.
export function existingToRow(doc) {
    return {
        uid: doc.docId || `${doc.s3Key}`,
        docType: doc.docType || '',
        docLabel: doc.docLabel || '',
        file: null,
        existing: doc,
    };
}

/**
 * Walks the editor rows and produces the `extraDocs` metadata array the backend
 * expects, appending each NEW row's file to `fd` under the repeated `extra_docs`
 * part — in the SAME order the s3Key-less entries appear in the array. Returns
 * `{ error }` (a user-facing message) when a row is incomplete.
 */
export function collectExtraDocs(rows, fd) {
    const extraDocs = [];
    for (const r of rows || []) {
        if (r.existing && r.existing.s3Key) {
            extraDocs.push(r.existing); // kept doc — carries its own s3Key
            continue;
        }
        if (!r.docType) return { error: 'Select a document type for each added document.' };
        if (r.docType === 'OTHER' && !(r.docLabel && r.docLabel.trim())) {
            return { error: "Enter a name for documents of type 'Other'." };
        }
        if (!r.file) return { error: 'Choose a file for each added document.' };
        extraDocs.push({
            docType: r.docType,
            docLabel: r.docType === 'OTHER' ? r.docLabel.trim() : null,
        });
        fd.append('extra_docs', r.file);
    }
    return { extraDocs };
}

/**
 * Dynamic "add more documents" editor. Controlled via `rows` / `setRows`.
 * Each row: { uid, docType, docLabel, file, existing }. `onView(doc)` opens a
 * saved document. Reused by the ABD appointment and non-ABD additional-docs modals.
 */
export default function ExtraDocsEditor({ rows, setRows, onView, existingCount = 0, categories = EXTRA_DOC_CATEGORIES }) {
    const total = existingCount + rows.length;
    const addRow = () => {
        if (total >= MAX_EXTRA_DOCS) return;
        setRows((prev) => [
            ...prev,
            {
                uid: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
                docType: '',
                docLabel: '',
                file: null,
                existing: null,
            },
        ]);
    };
    const removeRow = (uid) => setRows((prev) => prev.filter((r) => r.uid !== uid));
    const update = (uid, patch) =>
        setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Additional Documents ({total}/{MAX_EXTRA_DOCS})</Typography>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    disabled={total >= MAX_EXTRA_DOCS}
                >
                    Add more documents
                </Button>
            </Box>
            {rows.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                    No additional documents added.
                </Typography>
            )}
            <Grid container spacing={1.5}>
                {rows.map((r) => {
                    const isExisting = !!(r.existing && r.existing.s3Key);
                    return (
                        <Grid item xs={12} key={r.uid}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                                <Grid container spacing={1.5} alignItems="center">
                                    {isExisting ? (
                                        <>
                                            <Grid item xs={12} sm={5}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {r.existing.docLabel || categoryLabel(r.existing.docType)}
                                                </Typography>
                                                {r.existing.fileName && (
                                                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                        {r.existing.fileName}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={8} sm={6}>
                                                <Link component="button" type="button" variant="caption" onClick={() => onView && onView(r.existing)}>
                                                    View
                                                </Link>
                                            </Grid>
                                        </>
                                    ) : (
                                        <>
                                            <Grid item xs={12} sm={r.docType === 'OTHER' ? 4 : 7}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Document Type</InputLabel>
                                                    <Select
                                                        label="Document Type"
                                                        value={r.docType}
                                                        onChange={(e) => update(r.uid, { docType: e.target.value })}
                                                    >
                                                        {categories.map((c) => (
                                                            <MenuItem key={c.value} value={c.value}>
                                                                {c.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            {r.docType === 'OTHER' && (
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label="Document Name"
                                                        value={r.docLabel}
                                                        onChange={(e) => update(r.uid, { docLabel: e.target.value })}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item xs={8} sm={4}>
                                                <Button variant="outlined" size="small" component="label">
                                                    {r.file ? 'Replace' : 'Choose File'}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        onChange={(e) => update(r.uid, { file: e.target.files?.[0] || null })}
                                                    />
                                                </Button>
                                                {r.file && (
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        display="block"
                                                        title={r.file.name}
                                                        sx={{ mt: 0.5, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                    >
                                                        {r.file.name}
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={4} sm={1} textAlign="right">
                                        <IconButton size="small" color="error" onClick={() => removeRow(r.uid)}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}
